import * as React from 'react';
import { useParams } from 'react-router';
import {
  Box, Chip, CircularProgress, FormControl, InputLabel, 
  MenuItem, Select, Table, TableBody, TableCell, TableHead, 
  TableRow, Toolbar, Typography, Switch, FormControlLabel,
  Fab, Snackbar
} from '@mui/material';
import { ContentCopyRounded } from '@mui/icons-material';
import { useFetchAllContactsQuery, useToggleEmailValidityMutation } from '../../store/club/ClubApi';
import { useFetchTeamsQuery } from '../../store/team/TeamApi';
import { myConstants } from '../../shares/Constants';


/*
 * Die Liste der Kontakte kann nach verschiedenen
 * Bereichen gefiltert werden. Diese werden hier
 * mit den Bezeichnungen vorgefiltert
 * Hier geht es darum zu pflegen, zu welchen
 * Mannschaften die Betreuer gehören
 */
const RESPONSIBLE_LABELS = 
{
  0: 'Alle',
  1: 'U8',
  2: 'U10',
  10: 'w',
  11: 'wU8',
  12: 'wU10',
  20: 'm',
  21: 'mU8',
  22: 'mU10',
};

/*
 * Filterung nach den Rollen, den die Personen
 * im Club genutzt haben
 */
const ROLE_LABELS = 
{
  supervisor: 'BetreuerIn', 
  contact: 'AnsprechpartnerIn',
  responsible: 'TrainerIn',
};


export default function ContactAdmin() 
{
  const {year} = useParams();
  const {data: contacts, isLoading } = useFetchAllContactsQuery(year);
  const [toggleEmailValidity] = useToggleEmailValidityMutation();

  const [filterResponsible, setFilterResponsible] = React.useState('');
  const [onlyValid, setOnlyValid] = React.useState(false);
  const [filterClubLabel, setFilterClubLabel] = React.useState('NONE');
  const [copied, setCopied] = React.useState(false);
  const { data : teams } = useFetchTeamsQuery({ year, clubLabel: '' });
  const [filterByTeamStatus, setFilterByTeamStatus] = React.useState(false);
  


  if (isLoading)
  {
    return <CircularProgress />;
  }

  /*
   * Filterfunktion für die empfangenen Kontakte später prüfen
   */
  const filtered = (contacts ?? []).filter(
    role => {
      // Filter nach der Zugehörigkeit
      if (filterResponsible !== '' && String(role.responsible_for) !== filterResponsible)
      {
        return false;
      } 

      if (onlyValid && !role.user.is_email_valid)
      {
        return false;
      }

      if (filterClubLabel !== 'NONE' && String(role.club.label) !== filterClubLabel)
      {
        return false;
      }

      if (filterByTeamStatus)
      {
        const hasActiveTeam = (teams ?? []).some(
          team => team.clubLabel === role.club.label
          && (team.status ?? myConstants.teamModel.status.registered) === myConstants.teamModel.status.registered
        );
        if (!hasActiveTeam) return false;
      }

      return true;
    }
  );

  // Verarbeiten des Kopier-Handlers
  const handleCopyEmails = () => 
  {
    // Wir schnappen uns alle E-Mails aus den bereits gefilterten Einträgen
    const emails = filtered.map(role => role.user.email).join('; ');
    // nun kopieren wir diese Daten ins Clipboard.... 
    navigator.clipboard.writeText(emails);
    // und jetzt schreiben wir fort, dass wir die kopiert haben: 
    setCopied(true);
  };

  // Wir erstellen eine Übersicht über die Vereine, in denen die Personen
  // sind, damit wir auch danach filtern können:
  // Wir filtern im zweiten Schritt, damit wir auch wirklich immer nur den gleichen 
  const clubLabels = contacts
    .map( role => ({ label: role.club.label, name: role.club.name }))
    .filter((club, index, self) => self.findIndex( c => c.label === club.label ) === index )
    .sort((a,b) => a.name.localeCompare(b.name));

  return (
    <Box>
      <Toolbar disbaleGutters sx={{ gap:2, flexWrap: 'wrap', mb:1 }}>
        <FormControl size="small" sx={{ minWidth:160 }}>
          <InputLabel>Verein</InputLabel>
          <Select
            value={filterClubLabel}
            label="Verein"
            onChange={ event => setFilterClubLabel(event.target.value) }
          >
            <MenuItem value="NONE">Alle</MenuItem>
            {
              clubLabels.map(
                club => (
                  <MenuItem key={club.label} value={club.label}>{club.name}</MenuItem>
                )
              )
            }
          </Select>
        </FormControl>        
        <FormControl size="small" sx={{ minWidth:160 }}>
          <InputLabel>Zuständigkeit</InputLabel>
          <Select
            value={filterResponsible}
            label="Zuständigkeit"
            onChange={ event => setFilterResponsible(event.target.value) }
          >
            <MenuItem value="">Alle</MenuItem>
            {
              Object.entries(RESPONSIBLE_LABELS).map(
                ([value, label]) => (
                  <MenuItem key={value} value={value}>{label}</MenuItem>
                )
              )
            }
          </Select>
        </FormControl>
        <FormControlLabel
          control={
            <Switch checked={onlyValid} onChange={event => setOnlyValid(event.target.checked)} />             
          }
          label="Nur gültige E-Mails"
        >
        <Typography variant="body2" color="text.secondary">
          {filtered.length} Kontakt{filtered.length !== 1 ? 'e' : ''}
        </Typography>
        </FormControlLabel>
        <FormControlLabel
          control={
            <Switch checked={filterByTeamStatus} onChange={event => setFilterByTeamStatus(event.target.checked)} />
          }
          label="Nur mit aktiven Teams"
        />
      </Toolbar>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Verein</TableCell>
            <TableCell>Name</TableCell>
            <TableCell>E-Mail</TableCell>
            <TableCell>Mobil</TableCell>
            <TableCell>Rolle</TableCell>
            <TableCell>Zuständig</TableCell>
            <TableCell>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filtered.map(role => (
            <TableRow key={role.id}>
              <TableCell>{role.club.name}</TableCell>
              <TableCell>{role.user.first_name} {role.user.last_name}</TableCell>
              <TableCell>{role.user.email}</TableCell>
              <TableCell>{role.user.mobile}</TableCell>
              <TableCell>{ROLE_LABELS[role.role] ?? role.role}</TableCell>
              <TableCell>{RESPONSIBLE_LABELS[role.responsible_for] ?? role.responsible_for}</TableCell>
              <TableCell>
                <Chip
                  size="small"
                  label={role.user.is_email_valid ? 'Gültig' : 'Ungültig'}
                  color={role.user.is_email_valid ? 'success' : 'error'}
                  onClick={() => toggleEmailValidity({
                    year,
                    clubId: role.club.id,
                    userId: role.user.id,
                  })}
                  sx={{ cursor: 'pointer' }}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Fab
        size="small"
        color="primary"
        onClick={handleCopyEmails}
        sx={{ position: 'fixed', bottom: 32, right: 32 }}
        title={`${filtered.length} E-Mail-Adressen kopieren`}
      >
        <ContentCopyRounded/>
      </Fab>      
      
      <Snackbar
        open={copied}
        autoHideDuration={2000}
        onClose={() => setCopied(false)}
        message={`${filtered.length} E-Mail-Adresse${filtered.length !== 1 ? 'n':''} kopiert`}
      />

    </Box>
  );
}